import {
  ApiError,
  GuestLoyaltyBalance,
  LoyaltyEmbeddedCheckoutSession,
  LoyaltyProgramResponse,
} from '@/types';
import { isLoyaltyMockActive, mockGetProgram, mockSignup } from './loyaltyMock';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = null;
  }

  public setToken(token: string) {
    this.token = token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Don't set Content-Type for FormData - browser will set it with boundary
    const isFormData = options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // BES-7690: backend error responses use the `error` key (and `failureReason`
        // for a structured charge code). Surface both so the portal shows the real
        // message + code instead of a bare "HTTP 400".
        throw new ApiError(
          errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData.code || errorData.failureReason
        );
      }

      // Gracefully handle empty bodies (e.g., 204 No Content or empty 200)
      if (response.status === 204) {
        return {} as T;
      }

      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type') || '';
      if (
        contentLength === '0' ||
        (!contentType.includes('application/json') && !contentType.includes('json'))
      ) {
        return {} as T;
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error instanceof Error ? error.message : 'Network error', 0);
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }) as Promise<T>;
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
    }) as Promise<T>;
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }) as Promise<T>;
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }) as Promise<T>;
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }) as Promise<T>;
  }

  // BES-7690: fire-and-forget telemetry that survives tab-close (keepalive) and
  // keeps the auth header. Must never throw.
  beacon(endpoint: string, data: unknown): void {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      void fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // telemetry must never throw
    }
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// BES-7690: report a guest-portal payment failure (SDK tokenization or charge)
// to the backend so it is queryable in Datadog. Fire-and-forget.
export function reportPaymentClientEvent(
  stage: 'tokenize' | 'purchase',
  error: unknown,
  context?: Record<string, unknown>
): void {
  const err = error as { message?: string; code?: string; name?: string } | undefined;
  let raw: string | undefined;
  try {
    raw = JSON.stringify(error);
  } catch {
    raw = String(error);
  }
  apiClient.beacon('/upsells/payment-client-event', {
    stage,
    error: { message: err?.message ?? String(error), code: err?.code, name: err?.name, raw },
    context,
  });
}

// Auth API endpoints
export interface ValidateCodeRequest {
  code: string;
  page: string;
}

export interface ValidateCodeResponse {
  token: string;
}

export const authApi = {
  validateCode: (code: string, page: string): Promise<ValidateCodeResponse> =>
    apiClient.post<ValidateCodeResponse>('/validate', { code, page }),
};

// Check-in form API endpoints
export interface CheckInFormResponse {
  templateId: string;
  markup: string;
  css: string;
  fieldsSchema: Array<{
    name: string;
    required: boolean;
    type?: string;
  }>;
  submittedAt: string | null;
  listingId: number;
  customHtml?: boolean;
  submissionScript?: string;
  numOfAdults?: number;
  /** Listing max occupancy minus the primary signer, for capped guest-info. */
  maxAccompanyingGuests?: number | null;
  /** BES-9331: registry form components in the markup (labels translated). */
  formComponents?: import('@besty/shared/formComponents').ExtractedFormComponent[];
}

export interface SubmitFormRequest {
  values: Record<string, any>;
}

export interface SubmitFormResponse {
  ok: boolean;
}

export const checkinApi = {
  getForm: (language?: string): Promise<CheckInFormResponse> =>
    apiClient.get<CheckInFormResponse>(`/checkin/form${language ? `?language=${language}` : ''}`),
  submitForm: (values: Record<string, any>): Promise<SubmitFormResponse> =>
    apiClient.post<SubmitFormResponse>('/checkin/submit', { values }),
};

// Guidebooks API endpoints
export interface GuidebookSection {
  id: string;
  name: string;
  icon: string; // Lucide icon name
  order: number;
  pages: Array<{
    id: string;
    title: string;
    html: string;
    order: number;
  }>;
}

export interface GuidebookUiResponse {
  sections: GuidebookSection[];
  listingId: number | null;
  checkoutEnabled?: boolean;
  checkoutCompleted?: boolean;
}

export interface CheckoutResponse {
  ok: boolean;
}

export interface ListingStatusResponse {
  status: 'no_active_stay' | 'active_stay';
  listingId: number;
  listingName: string;
  reservationId?: number;
  tokenValid?: boolean;
}

export interface ListingVerifyResponse {
  token: string;
}

export const guidebooksApi = {
  getUi: (language?: string): Promise<GuidebookUiResponse> =>
    apiClient.get<GuidebookUiResponse>(`/guidebooks/ui${language ? `?language=${language}` : ''}`),
  getUiPreview: (listingId: number, language?: string): Promise<GuidebookUiResponse> =>
    apiClient.get<GuidebookUiResponse>(
      `/guidebooks/ui-preview?listingId=${listingId}${language ? `&language=${language}` : ''}`
    ),
  checkout: (): Promise<CheckoutResponse> =>
    apiClient.post<CheckoutResponse>('/guidebooks/checkout'),
  listingStatus: (listingCode: string, token?: string): Promise<ListingStatusResponse> =>
    apiClient.post<ListingStatusResponse>('/guidebooks/listing-status', { listingCode, token }),
  listingVerify: (data: {
    listingCode: string;
    verificationType: 'name' | 'contact';
    value: string;
    arrivalDate: string;
    departureDate: string;
  }): Promise<ListingVerifyResponse> =>
    apiClient.post<ListingVerifyResponse>('/guidebooks/listing-verify', data),
};

// Portal API endpoints
export type PortalItemKey =
  | 'rental_agreement'
  | 'damage_waiver'
  | 'check_in_form'
  | 'guidebook'
  | 'upsells';

export interface PortalItem {
  key: PortalItemKey;
  title: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'view_only';
  route: string;
  // Host-specific subtitle override; falls back to the portal's default copy.
  blurb?: string;
}

export interface PortalUiResponse {
  items: PortalItem[];
  scopes: string[];
}

export interface PortalLookupResponse {
  status: 'matched' | 'multi_match' | 'no_match';
  nonce?: string;
  nonces?: string[];
}

export interface PortalReservationEntry {
  token: string;
  reservationId: number;
  listingName: string;
  listingAddress: string;
}

// Single-match path returns { token, reservationId }. Multi-match path returns
// { reservations: [...] } so the frontend can render a picker.
export type PortalVerifyResponse =
  | { token: string; reservationId: number }
  | { reservations: PortalReservationEntry[] };

export interface PortalStatusResponse {
  tokenValid: boolean;
  reservationId?: number;
  departureTimestamp?: string | null;
}

export const portalApi = {
  // Resolves 200 when the hostCode (or, on white-label origins, the request
  // Origin) maps to a host. 404 means the link is bad; ApiError.status === 404.
  checkHost: (hostCode: string): Promise<{ ok: true }> =>
    apiClient.get<{ ok: true }>(`/portal/host?hostCode=${encodeURIComponent(hostCode)}`),
  lookup: (data: {
    guestName: string;
    arrivalDate: string;
    departureDate: string;
    hostCode: string;
  }): Promise<PortalLookupResponse> => apiClient.post<PortalLookupResponse>('/portal/lookup', data),
  verify: (data: { nonce?: string; nonces?: string[] }): Promise<PortalVerifyResponse> =>
    apiClient.post<PortalVerifyResponse>('/portal/verify', data),
  status: (token: string): Promise<PortalStatusResponse> =>
    apiClient.post<PortalStatusResponse>('/portal/status', { token }),
  getUi: (language?: string): Promise<PortalUiResponse> =>
    apiClient.get<PortalUiResponse>(`/portal/ui${language ? `?language=${language}` : ''}`),
};

export interface LoyaltySignupBody {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  tosAcceptedVersion?: string | null;
  /** Required for tiered programs: which tier the guest chose. */
  tierKey?: string;
}

// Loyalty signup talks to the guest portal backend (same auth + CORS as every
// other portal call), which proxies into the direct-booking loyalty business
// logic in-process. The frontend never touches /api/directBookingSite/*.
//
// getProgram returns { program, enrolledMembership }. enrolledMembership is
// non-null when the current reservation has already kicked off a signup, so
// the portal can render a member state instead of re-offering the join CTA.
//
// signup creates the direct-booking guest account and opens an embedded Stripe
// Checkout session. The frontend mounts the embedded checkout with the
// returned clientSecret + connectedAccountId; there is no redirect.
export const loyaltyApi = {
  getProgram: (): Promise<LoyaltyProgramResponse> => {
    if (isLoyaltyMockActive()) return Promise.resolve(mockGetProgram());
    return apiClient.get<LoyaltyProgramResponse>('/loyalty/program');
  },
  signup: (body: LoyaltySignupBody): Promise<LoyaltyEmbeddedCheckoutSession> => {
    if (isLoyaltyMockActive()) return Promise.resolve(mockSignup());
    return apiClient.post<LoyaltyEmbeddedCheckoutSession>('/loyalty/signup', body);
  },
  getGuestBalance: (): Promise<GuestLoyaltyBalance> =>
    apiClient.get<GuestLoyaltyBalance>('/loyalty/guest-balance'),
};

export { ApiError };
